import {Card, Col, DatePicker, Row, Table} from "antd"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import React, {useCallback, useEffect, useMemo} from "react"
import moment from "moment"
import {Loading} from "../../components/Loading/Loading"
import {limitOptionsSelect, securityChangeModeOptions, securityObjectOptions} from "../../helps/optionsSelect"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {NavLink, useHistory, useLocation} from "react-router-dom"
import {UserTypeEnum} from "../../types/types"
import {center} from "../../helps/tableHelper"
import {Helmet} from "react-helmet-async"
import {FormSelect} from "../../components/Form/FormSelect"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {getSecurityLogs, setSecurityLogFilter, setSecurityLogs, setWSSecurityLog} from "../../store/securityLogReducer"
import {getRowClassNameSecurityLog} from "../../helps/getRowClassNameSecurityLog"
import {useGetCompanies, useGetDevices, useGetObjectGroups, useGetObjects, useGetUsers} from "../../hooks/useFetchHooks"
import {SecurityLogResponse} from "../../models/response"
import {getTimeZoneDiffString} from "../../helps/timezoneDiff"
import {FormInput} from "../../components/Form/FormInput"
import locale from "antd/es/date-picker/locale/ru_RU"
import dayjs from "dayjs"

export const SecurityLogs = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const securityLogs = useSelector((state: StateType) => state.securityLogReducer.securityLogs)
    const filter = useSelector((state: StateType) => state.securityLogReducer.filter)
    const ws = useSelector((state: StateType) => state.securityLogReducer.ws)
    const controller = useMemo(() => new AbortController(), [])

    const getCompanyParams = useMemo(() => ({excludeSystem: true}), [])
    const companies = useGetCompanies(getCompanyParams)

    const objectGroupsQuery = useMemo(() => ({
        idCompany: filter.idCompany,
    }), [filter.idCompany])
    const objectGroups = useGetObjectGroups(objectGroupsQuery)

    const objectsQuery = useMemo(() => ({
        idCompany: filter.idCompany,
        idObjectGroup: filter.idObjectGroup,
    }), [filter.idCompany, filter.idObjectGroup])
    const objects = useGetObjects(objectsQuery)

    const devicesQuery = useMemo(() => ({
        idCompany: filter.idCompany,
        idObjectGroup: filter.idObjectGroup,
        idObject: filter.idObject,
    }), [filter.idCompany, filter.idObjectGroup, filter.idObject])
    const devices = useGetDevices(devicesQuery)

    const usersQuery = useMemo(() => ({
        idCompany: filter.idCompany,
    }), [filter.idCompany, filter.idObjectGroup, filter.idObject])
    const users = useGetUsers(usersQuery)

    const columns = useMemo(() => {
        const cols = []

        cols.push(
            {
                title: 'Дата',
                dataIndex: 'date',
                key: 'date',
                align: center,
            })

        if (profile?.idCompany === ID_SYSTEM_COMPANY) cols.push({
            title: 'Компания',
            dataIndex: 'company',
            key: 'company',
            align: center,
            render: (company: {id: number, name: string}) => {
                return <NavLink to={`/company/${company.id}`}>{company.name}</NavLink>
            }
        })

        cols.push(
            {
                title: 'Объект',
                dataIndex: 'object',
                key: 'object',
                align: center,
                render: (object: {id: number, name: string}) => {
                    return <NavLink to={`/object/${object.id}`}>{object.name}</NavLink>
                }
            },
            {
                title: 'Оборудование',
                dataIndex: 'device',
                key: 'device',
                align: center,
                render: (device?: {id: number, name: string} | null) => {
                    if (!device) return null
                    return <NavLink to={`/psLeakDevice/${device.id}`}>{device.name}</NavLink>
                }
            },
            {
                title: 'Сообщение',
                dataIndex: 'action',
                key: 'action',
                align: center,
            },
            {
                title: 'Режим',
                dataIndex: 'changeModeUser',
                key: 'changeModeUser',
                align: center,
                render: (changeModeUser: {changeMode: SecurityLogResponse["changeMode"], user?: {id: number, name: string, email: string}}) =>
                {
                    const {changeMode, user} = changeModeUser
                    switch (changeMode)
                    {
                        case "MANUAL":
                            return `Ручной${user ? ` (${user.name} - ${user.email})` : ''}`
                        case "SCHEDULE":
                            return "Расписание"
                        case "DEVICE":
                            return "Устройство"
                        default:
                            return changeMode
                    }
                },
            },
        )

        return cols
    }, [profile])

    useEffect(() => {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter)) {
            dispatch(setSecurityLogFilter({...filterString}))
        } else if (location.search !== filterToString(filter)) {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getSecurityLogsFunc = useCallback((withoutLoadingIndicator = false) => {
        if (location?.search) {
            dispatch(getSecurityLogs({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() => {
        getSecurityLogsFunc()

        return () => {
            dispatch(setSecurityLogs(null))
        }
    }, [dispatch, getSecurityLogsFunc])

    useEffect(() => {
        return () => {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() => {
        if (ws) {
            getSecurityLogsFunc(true)
        }
        dispatch(setWSSecurityLog(null))
    }, [dispatch, ws, getSecurityLogsFunc])

    const data = useMemo(() => securityLogs?.data?.map(i => {
        return {
            key: i.id,
            id: i.id,
            action: i.action,
            type: i.type,
            company: {id: i.idCompany, name: i.nameCompany},
            object: {id: i.idObject, name: i.nameObject},
            device: i.idDevice ? {id: i.idDevice, name: i.nameDevice} : null,
            date: `${moment(i.date).format('DD.MM.YYYY HH:mm')} ${getTimeZoneDiffString(i.timeZone)}`,
            changeModeUser: {changeMode: i.changeMode, user: i.idUser ? {id: i.idUser, name: i.nameUser, email: i.emailUser} : null},
        }
    }), [securityLogs])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({label: i.name, value: i.id})), [companies])
    const objectGroupsOptions = useMemo(() => objectGroups?.data?.map(i => ({label: i.name, value: i.id})), [objectGroups])
    const objectOptions = useMemo(() => objects?.data?.map(i => ({label: i.name, value: i.id})), [objects])
    const deviceOptions = useMemo(() => devices?.data?.map(i => ({label: i.name, value: i.id})), [devices])
    const userOptions = useMemo(() => users?.data?.map(i => ({label: `${i.name} - ${i.email}`, value: i.id})), [users])

    return (
        <div className="tabled">
            <Helmet>
                <title>Логи охраны</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title="Логи охраны"
                          extra={
                              <div className="table-title-extra">
                                  <FormInput placeholder="Поиск"
                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                             value={filter?.search}
                                             name="search"
                                             onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                             allowClear
                                  />

                                  <DatePicker locale={locale}
                                              showTime
                                              placeholder="Дата от"
                                              format="DD.MM.YYYY HH:mm"
                                              showSecond={false}
                                              style={{height: 50, width: 150}}
                                              value={filter.dateFrom ? dayjs(filter.dateFrom) : null}
                                              onChange={(value) => history.replace({search: filterToString({...filter, dateFrom: value ? value.format("YYYY-MM-DD HH:mm") : null, offset: 0})})}
                                              disabledDate={(currentDate) => currentDate > dayjs(new Date())}
                                  />

                                  <DatePicker locale={locale}
                                              showTime
                                              placeholder="Дата по"
                                              format="DD.MM.YYYY HH:mm"
                                              showSecond={false}
                                              style={{height: 50, width: 150}}
                                              value={filter.dateTo ? dayjs(filter.dateTo) : null}
                                              onChange={(value) => history.replace({search: filterToString({...filter, dateTo: value ? value.format("YYYY-MM-DD HH:mm") : null, offset: 0})})}
                                              disabledDate={(currentDate) => filter.dateFrom ? currentDate < dayjs(filter.dateFrom) || currentDate > dayjs(new Date()) : currentDate > dayjs(
                                                  new Date())}
                                  />

                                  {
                                      profile?.idCompany === ID_SYSTEM_COMPANY
                                          ?
                                          <FormSelect value={filter?.idCompany}
                                                      containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                                      dropdownStyle={{minWidth: 'min-content'}}
                                                      onChange={(value) => history.replace({
                                                          search: filterToString({
                                                              ...filter,
                                                              idCompany: value,
                                                              offset: 0,
                                                              idObjectGroup: '',
                                                              idObject: '',
                                                              idDevice: '',
                                                              idUser: '',
                                                          }),
                                                      })}
                                                      options={companyOptions}
                                                      placeholder="Компания"
                                                      allowClear
                                                      placement="bottomRight"
                                          />
                                          : null
                                  }

                                  <FormSelect value={filter?.idObjectGroup}
                                              containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({
                                                  search: filterToString({
                                                      ...filter,
                                                      idObjectGroup: value,
                                                      offset: 0,
                                                      idObject: '',
                                                      idDevice: '',
                                                      idUser: '',
                                                  }),
                                              })}
                                              options={objectGroupsOptions}
                                              placeholder="Группа"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.idObject}
                                              containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({
                                                  search: filterToString({
                                                      ...filter,
                                                      idObject: value,
                                                      offset: 0,
                                                      idDevice: '',
                                                      idUser: '',
                                                  }),
                                              })}
                                              options={objectOptions}
                                              placeholder="Объект"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.idDevice}
                                              containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({
                                                  search: filterToString({
                                                      ...filter,
                                                      idDevice: value,
                                                      offset: 0,
                                                      idUser: '',
                                                  }),
                                              })}
                                              options={deviceOptions}
                                              placeholder="Устройство"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  {
                                      profile?.idUserType === UserTypeEnum.ADMIN_SYSTEM || profile?.idUserType === UserTypeEnum.ADMIN_SYSTEM
                                      ? <FormSelect value={filter?.idUser}
                                                    containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                                    dropdownStyle={{minWidth: 'min-content'}}
                                                    onChange={(value) => history.replace({
                                                        search: filterToString({
                                                            ...filter,
                                                            idUser: value,
                                                            offset: 0,
                                                        }),
                                                    })}
                                                    options={userOptions}
                                                    placeholder="Пользователь"
                                                    allowClear
                                                    placement="bottomRight"
                                          />
                                          : null
                                  }

                                  <FormSelect value={filter?.changeMode}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, changeMode: value, offset: 0})})}
                                              options={securityChangeModeOptions}
                                              placeholder="Режим"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: 'auto'}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            {
                                securityLogs
                                    ? <Table columns={columns}
                                             dataSource={data}
                                             pagination={filter?.limit ? {
                                                 position: securityLogs?.data?.length >= 10 ? ['bottomLeft', 'topLeft'] : ['bottomLeft'],
                                                 pageSize: filter.limit,
                                                 hideOnSinglePage: true,
                                                 total: securityLogs.total,
                                                 current: filter?.offset / filter?.limit + 1,
                                                 onChange: (page, pageSize) => {
                                                     const obj = {
                                                         limit: pageSize,
                                                         offset: (page - 1) * pageSize,
                                                     }
                                                     history.replace({search: filterToString({...filter, ...obj})})
                                                 },
                                                 showSizeChanger: false,
                                             } : false}
                                             className="ant-border-space no-hover"
                                             rowClassName={(i) => getRowClassNameSecurityLog(i.type)}
                                    />
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}
